import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);
const API = '/api';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasSites, setHasSites] = useState(false);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem('plan_token');
    if (!token) { setLoading(false); return; }
    try {
      const res = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setUser(await res.json());
      else localStorage.removeItem('plan_token');
    } catch(e) { localStorage.removeItem('plan_token'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  useEffect(() => {
    if (!user) { setHasSites(false); return; }
    const token = localStorage.getItem('plan_token');
    fetch(`${API}/sites/`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(s => setHasSites((s||[]).length > 0))
      .catch(() => {});
  }, [user]);

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ username: email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Login failed');
    localStorage.setItem('plan_token', data.access_token);
    setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('plan_token');
    setUser(null);
    window.location.href = '/login';
  }, []);

  const role = user?.role;
  const isAdmin        = ['super_admin','admin'].includes(role);
  const isSuperAdmin   = role === 'super_admin';
  const isAgency       = user?.org_type === 'agency';
  const canEdit        = ['super_admin','admin','account_manager','senior_planner','junior_planner'].includes(role);
  const canCreatePlans = ['super_admin','admin','account_manager','senior_planner'].includes(role);
  const canApprovePlans= ['super_admin','admin','account_manager','senior_planner'].includes(role);
  const canEditCatalogue = ['super_admin','admin','senior_planner'].includes(role);
  const isManager      = ['super_admin','admin','account_manager','senior_planner'].includes(role);
  // Gemius/GDE ZE-code taxonomy (2026-10-05) — org-level flag, off by default; same
  // can_manage_taxonomy/can_import_taxonomy role sets as the backend (app/core/permissions.py).
  const taxonomyEnabled   = !!user?.taxonomy_enabled;
  const canManageTaxonomy = taxonomyEnabled && ['super_admin','admin','senior_planner'].includes(role);
  const canImportTaxonomy = taxonomyEnabled && ['super_admin','admin'].includes(role);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAdmin, isSuperAdmin, isAgency, canEdit, canCreatePlans, canApprovePlans, canEditCatalogue, isManager, role, hasSites, taxonomyEnabled, canManageTaxonomy, canImportTaxonomy }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
